import React, { useMemo, useState } from 'react';
import { Copy, X } from 'lucide-react';

interface TeacherContactModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  wechatId?: string;
  qrImageSrc?: string;
  mode?: 'contact' | 'notice';
}

const DEFAULT_WECHAT_ID = 'dao6257';
const DEFAULT_QR_SRC = '/contact/ruoxuan-wechat-qr.png';

const TeacherContactModal: React.FC<TeacherContactModalProps> = ({
  open,
  onClose,
  title,
  description,
  wechatId = DEFAULT_WECHAT_ID,
  qrImageSrc = DEFAULT_QR_SRC,
  mode = 'contact',
}) => {
  const [copyNotice, setCopyNotice] = useState('');

  const wechatText = useMemo(() => String(wechatId || DEFAULT_WECHAT_ID).trim() || DEFAULT_WECHAT_ID, [wechatId]);

  const copyWechatId = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(wechatText);
      } else {
        const temp = document.createElement('input');
        temp.value = wechatText;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setCopyNotice('微信号已复制');
      window.setTimeout(() => setCopyNotice(''), 1800);
    } catch {
      setCopyNotice(`复制失败，请手动添加 ${wechatText}`);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/45 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white border border-slate-200 shadow-2xl p-5 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100"
          aria-label="关闭咨询弹窗"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>
        <h3 className="text-lg font-semibold text-slate-900 pr-8">{title}</h3>
        <p className="mt-2 text-sm text-slate-600 leading-6">{description}</p>

        {mode === 'contact' ? (
          <>
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center justify-center">
              <img src={qrImageSrc} alt="若轩老师微信二维码" className="w-full max-w-[320px] h-auto rounded-lg bg-white" />
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm">
              <span className="text-slate-500">微信：</span>
              <button
                onClick={copyWechatId}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-emerald-700 hover:bg-emerald-100"
              >
                <span className="font-semibold">{wechatText}</span>
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>

            {copyNotice && (
              <div className="mt-3 text-xs text-emerald-600">{copyNotice}</div>
            )}
          </>
        ) : (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700 leading-6">
            功能正在开发中，后续版本开放。
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherContactModal;
